/**
 * Stores bootstrap/API failures to show the error page only after lead capture (registration + OTP).
 * Module-level store so axios interceptors and hooks can share state without circular context deps.
 */

import {CTA_TYPES} from 'Src/constants'
import {displayPhoneNumber} from './Helpers'
import {isPseudoAuthAuthenticated, TOKEN_STATE} from './useToken'
import {vehicleHasNoSellingPrice} from './vehicleSellingPrice'

export {getErrorScreenLeadSuccessMessage} from './errorScreenUserMessage'

export {vehicleHasNoSellingPrice} from './vehicleSellingPrice'

/** @typedef {'lang' | 'dealership' | 'request_types' | 'vehicle' | 'desking' | 'calculation_engine' | 'unknown'} TDeferredErrorSource */

/**
 * @typedef {Object} IDeferredInitializationError
 * @property {TDeferredErrorSource} source
 * @property {number} [httpStatus]
 * @property {string} [requestPath]
 * @property {Array<{ code?: string, title?: string, detail?: string }>} errors
 * @property {string} [message]
 * @property {string} [errorCause]
 * @property {string} capturedAt
 */

const ERROR_SCREEN_FLOW_SESSION_KEY = 'dr_error_screen_lead_flow'
const ERROR_SCREEN_DEFERRED_ERROR_KEY = 'dr_error_screen_deferred_error'

const persistDeferredErrorToSession = (error) => {
  if (!error) {
    return
  }

  try {
    globalThis.sessionStorage?.setItem(ERROR_SCREEN_DEFERRED_ERROR_KEY, JSON.stringify(error))
  } catch {
    /* quota / private mode */
  }
}

const removeDeferredErrorFromSession = () => {
  globalThis.sessionStorage?.removeItem(ERROR_SCREEN_DEFERRED_ERROR_KEY)
}

/** Restore in-memory deferred error after full page reload during registration. */
export const hydrateDeferredInitializationErrorFromSession = () => {
  if (deferredInitializationError) {
    return deferredInitializationError
  }

  const stored = globalThis.sessionStorage?.getItem(ERROR_SCREEN_DEFERRED_ERROR_KEY)

  if (!stored) {
    return null
  }

  try {
    deferredInitializationError = JSON.parse(stored)
  } catch {
    removeDeferredErrorFromSession()
  }

  return deferredInitializationError
}

const ensureDeferredErrorHydrated = () => {
  hydrateDeferredInitializationErrorFromSession()
}

/** @type {IDeferredInitializationError | null} */
let deferredInitializationError = null

/** When false, failures use the legacy immediate /error behavior (e.g. soft-pull / trade CTAs). */
let leadCaptureDeferralEnabled = false

/** Prevents slide-out auto-navigation from overriding /register after a deferred error redirect. */
let slideOutRedirectsPrevented = false

export const setLeadCaptureDeferralEnabled = (enabled) => {
  leadCaptureDeferralEnabled = !!enabled
}

export const shouldPreventSlideOutRedirects = () => slideOutRedirectsPrevented

export const setSlideOutRedirectsPrevented = (prevented) => {
  slideOutRedirectsPrevented = !!prevented
}

export const shouldDeferErrorsToLeadCapture = () => leadCaptureDeferralEnabled

export const isDeferrableLeadCaptureCta = (ctaType) =>
  ctaType !== CTA_TYPES.SOFT_PULL && ctaType !== CTA_TYPES.TRADE

/** Default CTAs only; guests during LOADING or UNAUTHENTICATED; never pseudo-auth users. */
export const shouldEnableLeadCaptureDeferral = (ctaType, tokenState) => {
  if (!isDeferrableLeadCaptureCta(ctaType)) {
    return false
  }

  if (isPseudoAuthAuthenticated()) {
    return false
  }

  return tokenState === TOKEN_STATE.UNAUTHENTICATED || tokenState === TOKEN_STATE.LOADING
}

export const getDeferredInitializationError = () => {
  ensureDeferredErrorHydrated()

  return deferredInitializationError
}

export const hasDeferredInitializationError = () => {
  ensureDeferredErrorHydrated()

  return deferredInitializationError !== null
}

export const markErrorScreenLeadFlowPending = () => {
  globalThis.sessionStorage?.setItem(ERROR_SCREEN_FLOW_SESSION_KEY, '1')
}

export const isErrorScreenLeadFlowPending = () => {
  if (globalThis.sessionStorage?.getItem(ERROR_SCREEN_FLOW_SESSION_KEY) === '1') {
    ensureDeferredErrorHydrated()

    if (!deferredInitializationError) {
      globalThis.sessionStorage?.removeItem(ERROR_SCREEN_FLOW_SESSION_KEY)
      slideOutRedirectsPrevented = false

      return false
    }

    slideOutRedirectsPrevented = true

    return true
  }

  return hasDeferredInitializationError()
}

export const setDeferredInitializationError = (error) => {
  if (!error) {
    deferredInitializationError = null

    return
  }

  deferredInitializationError = error
  markErrorScreenLeadFlowPending()
  persistDeferredErrorToSession(error)
  setSlideOutRedirectsPrevented(true)
}

export const clearDeferredInitializationError = () => {
  deferredInitializationError = null

  globalThis.sessionStorage?.removeItem(ERROR_SCREEN_FLOW_SESSION_KEY)
  removeDeferredErrorFromSession()

  slideOutRedirectsPrevented = false
}

/** Drop stale error-screen state when the user closes DR without completing registration. */
export const clearDeferredErrorOnSlideOutClose = () => {
  clearDeferredInitializationError()
}

/** Clear deferred error when the embed VIN changed (e.g. user opened a different vehicle). */
export const clearStaleDeferredInitializationError = ({vin} = {}) => {
  if (!vin) {
    return false
  }

  ensureDeferredErrorHydrated()

  const capturedVin = deferredInitializationError?.capturedVin

  if (capturedVin && capturedVin !== vin) {
    clearDeferredInitializationError()

    return true
  }

  return false
}

/**
 * Clear deferred error when the vehicle context no longer matches the captured failure.
 * Desking / calculation_engine failures are not cleared from price alone — they may fail
 * with a valid selling price (pricing engine, programs, etc.). Those clear on successful
 * desking load, VIN change, slide-out close, or OTP completion.
 */
export const clearDeferredErrorIfResolved = ({vehicle, embedVehicle, vin} = {}) => {
  if (!hasDeferredInitializationError() && !isErrorScreenLeadFlowPending()) {
    return false
  }

  if (clearStaleDeferredInitializationError({vin})) {
    return true
  }

  const cause = getDeferredErrorCause()
  const err = getDeferredInitializationError()

  if (cause === 'vehicle_not_in_inventory') {
    if (vehicle?.id) {
      clearDeferredInitializationError()

      return true
    }

    return false
  }

  if (
    (cause === 'no_selling_price' || cause === 'invalid_price') &&
    err?.source === 'vehicle'
  ) {
    if (vehicle?.id && !vehicleHasNoSellingPrice(vehicle, embedVehicle)) {
      clearDeferredInitializationError()

      return true
    }

    return false
  }

  return false
}

/**
 * @param {import('axios').AxiosError | { response?: { status?: number, data?: { errors?: unknown[] }, config?: { url?: string } }, message?: string }} error
 * @param {TDeferredErrorSource} source
 * @returns {IDeferredInitializationError}
 */
export const normalizeDeferredInitializationError = (error, source = 'unknown') => {
  const response = error?.response
  const apiErrors = Array.isArray(response?.data?.errors)
    ? response.data.errors.map(({code, title, detail}) => ({
        code,
        title,
        detail,
      }))
    : []

  const firstDetail = apiErrors[0]?.detail
  const message =
    firstDetail ||
    error?.message ||
    (source === 'calculation_engine'
      ? 'Our Calculation Engine is temporarily unavailable.'
      : 'A background request failed.')

  return {
    source,
    httpStatus: response?.status,
    requestPath: response?.config?.url,
    errors: apiErrors,
    message,
    capturedAt: new Date().toISOString(),
  }
}

/**
 * @param {import('axios').AxiosError | object} error
 * @param {TDeferredErrorSource} source
 */
const inferErrorCause = (normalized, source) => {
  if (normalized.message?.includes('Vehicle not in inventory')) {
    return 'vehicle_not_in_inventory'
  }

  switch (source) {
    case 'calculation_engine':
    case 'desking':
      return 'no_selling_price'
    case 'vehicle':
      return 'vehicle_not_in_inventory'
    case 'lang':
    case 'dealership':
    case 'request_types':
      return 'bootstrap_error'
    default:
      return 'unknown'
  }
}

/**
 * @param {import('axios').AxiosError | object} error
 * @param {TDeferredErrorSource} source
 * @param {{ errorCause?: string }} [options]
 */
export const captureDeferredInitializationError = (error, source = 'unknown', options = {}) => {
  if (!leadCaptureDeferralEnabled) {
    return null
  }

  const normalized = normalizeDeferredInitializationError(error, source)
  normalized.errorCause = options.errorCause || inferErrorCause(normalized, source)

  if (options.vin) {
    normalized.capturedVin = options.vin
  }

  setDeferredInitializationError(normalized)

  return normalized
}

export const getDeferredErrorCause = () => {
  const err = getDeferredInitializationError()

  return err?.errorCause || 'unknown'
}

/** Payload for lead / verification APIs (no stack traces or PII). */
export const getInitializationErrorPayload = () => {
  const err = getDeferredInitializationError()

  if (!err) {
    return undefined
  }

  return {
    source: err.source,
    http_status: err.httpStatus,
    request_path: err.requestPath,
    errors: err.errors,
    message: err.message,
    captured_at: err.capturedAt,
  }
}

/** Raw API detail (for logging/API payloads). Error page uses `?cause=` + Error.tsx copy instead. */
export const getDeferredErrorPageMessage = () => {
  const err = getDeferredInitializationError()

  return err?.message || null
}

/**
 * Path for error page after error-screen lead.
 * Uses existing Error.tsx logic (vehicle price), not a forced cause when price is present (e.g. 1).
 */
export const getDeferredErrorPagePath = ({vehicle, embedVehicle} = {}) => {
  const cause = getDeferredErrorCause()

  if (cause === 'no_selling_price' && vehicleHasNoSellingPrice(vehicle, embedVehicle)) {
    return '/error?cause=no_selling_price'
  }

  if (cause && cause !== 'unknown' && cause !== 'no_selling_price') {
    return `/error?cause=${encodeURIComponent(cause)}`
  }

  return '/error'
}

/** Suppress axios toasts while error-screen lead capture is in progress. */
export const shouldSuppressApiErrorToasts = () => isErrorScreenLeadFlowPending()

/**
 * Toast shown once after error-screen lead is created (before navigating to /error).
 * @param {string} [dealershipPhone]
 */
export const getDeferredErrorToastMessage = (dealershipPhone) => {
  const err = getDeferredInitializationError()

  if (!err) {
    return null
  }

  const first = err.errors?.[0]
  let description = first?.detail || err.message || 'A background request failed.'

  if (dealershipPhone) {
    description += ` Please call us at ${displayPhoneNumber(
      dealershipPhone
    )} for personalized assistance, or try again later.`
  } else {
    description += ' Try again later.'
  }

  if (first?.code) {
    description += ` <strong>Code: ${first.code}</strong>`
  }

  return {
    title: first?.title || 'Error',
    description,
    type: 'error',
    autohide: false,
  }
}
